import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiPlus, FiTrash2, FiSave, FiSearch, FiTarget, FiTrendingUp, 
  FiEdit3, FiX, FiHexagon, FiChevronDown, FiChevronUp, 
  FiDollarSign, FiInfo, FiActivity, FiLayers, FiFilter, 
  FiCalendar, FiCheckCircle, FiAlertTriangle, FiMinusCircle 
} = FiIcons;

const TABLES = {
  MATERIALS: 'rox_materials_1740635000',
  ITEMS: 'rox_items_1740635000',
  CURRENCIES: 'rox_event_currencies_1740638000',
  EVENTS: 'rox_events_1740640000',
  PACKS: 'rox_event_packs_1740640000'
};

export default function EventCalc() {
  const [activeTab, setActiveTab] = useState('saved');
  const [materials, setMaterials] = useState([]);
  const [items, setItems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [events, setEvents] = useState([]);
  const [savedPacks, setSavedPacks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterEventId, setFilterEventId] = useState('all');

  const [eventName, setEventName] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [currencyName, setCurrencyName] = useState('');
  const [currencyRate, setCurrencyRate] = useState(1);
  const [editingCurrency, setEditingCurrency] = useState(null);

  const [currentPackId, setCurrentPackId] = useState(null);
  const [packName, setPackName] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedCurrencyId, setSelectedCurrencyId] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    const [matsRes, itemsRes, currRes, eventsRes, packsRes] = await Promise.all([
      supabase.from(TABLES.MATERIALS).select('item_name, e_cost, category'),
      supabase.from(TABLES.ITEMS).select('item_name, e_cost, category'),
      supabase.from(TABLES.CURRENCIES).select('*').order('name'),
      supabase.from(TABLES.EVENTS).select('*').order('name'),
      supabase.from(TABLES.PACKS).select(`*, event:${TABLES.EVENTS}(*), currency:${TABLES.CURRENCIES}(*)`).order('created_at', { ascending: false })
    ]);

    if (matsRes.data) setMaterials(matsRes.data);
    if (itemsRes.data) setItems(itemsRes.data);
    if (currRes.data) setCurrencies(currRes.data);
    if (eventsRes.data) setEvents(eventsRes.data);
    if (packsRes.data) setSavedPacks(packsRes.data);
    setIsLoading(false);
  };

  const activeCurrency = useMemo(() => currencies.find(c => c.id === selectedCurrencyId), [currencies, selectedCurrencyId]);
  
  // FIX: Added allAvailable combination of items and materials for the search dropdown
  const allAvailable = useMemo(() => [...materials, ...items], [materials, items]);

  const calculations = useMemo(() => {
    let marketWorth = 0;
    let eventSpendCrystals = 0;
    const rate = activeCurrency?.crystal_rate || 0;

    selectedItems.forEach(item => {
      const qty = item.quantity || 0;
      marketWorth += (item.e_cost || 0) * qty;
      eventSpendCrystals += (item.unit_event_cost || 0) * qty * rate;
    });

    const efficiency = eventSpendCrystals > 0 ? (marketWorth / eventSpendCrystals) : 0;
    
    let status = 'none';
    if (eventSpendCrystals > 0) {
      if (efficiency >= 1.15) status = 'worth';
      else if (efficiency < 1.10) status = 'avoid';
      else status = 'breakeven';
    }

    return { marketWorth, eventSpendCrystals, saving: marketWorth - eventSpendCrystals, efficiency, status };
  }, [selectedItems, activeCurrency]);

  const getStatusConfig = (status) => {
    switch(status) {
      case 'worth': return { label: 'Worth It!', color: 'bg-green-600', icon: FiCheckCircle };
      case 'avoid': return { label: 'Do Not Buy!', color: 'bg-red-600', icon: FiAlertTriangle };
      case 'breakeven': return { label: 'Break Even', color: 'bg-amber-500', icon: FiMinusCircle };
      default: return null;
    }
  };

  const saveEvent = async () => {
    if (!eventName) return;
    const { error } = editingEvent 
      ? await supabase.from(TABLES.EVENTS).update({ name: eventName }).eq('id', editingEvent.id)
      : await supabase.from(TABLES.EVENTS).insert([{ name: eventName }]);
    if (!error) { setEventName(''); setEditingEvent(null); fetchData(); }
  };

  const saveCurrency = async () => {
    if (!currencyName) return;
    const { error } = editingCurrency 
      ? await supabase.from(TABLES.CURRENCIES).update({ name: currencyName, crystal_rate: currencyRate }).eq('id', editingCurrency.id)
      : await supabase.from(TABLES.CURRENCIES).insert([{ name: currencyName, crystal_rate: currencyRate }]);
    if (!error) { setCurrencyName(''); setCurrencyRate(1); setEditingCurrency(null); fetchData(); }
  };

  const savePack = async () => {
    if (!packName || !selectedCurrencyId || !selectedEventId) return;
    const data = {
      name: packName,
      event_id: selectedEventId,
      currency_id: selectedCurrencyId,
      items: selectedItems,
      target_market_worth: calculations.marketWorth
    };
    const { error } = currentPackId 
      ? await supabase.from(TABLES.PACKS).update(data).eq('id', currentPackId)
      : await supabase.from(TABLES.PACKS).insert([data]);
    if (!error) { resetPackForm(); fetchData(); setActiveTab('saved'); }
  };

  const resetPackForm = () => {
    setCurrentPackId(null); setPackName(''); setSelectedEventId(''); setSelectedCurrencyId(''); setSelectedItems([]);
  };

  const filteredPacks = useMemo(() => {
    if (filterEventId === 'all') return savedPacks;
    return savedPacks.filter(p => p.event_id === filterEventId);
  }, [savedPacks, filterEventId]);

  return (
    <div className="space-y-8">
      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 p-1.5 bg-gray-100 rounded-[2rem] w-fit">
        {[
          { id: 'saved', label: 'Event Packs', icon: FiLayers },
          { id: 'create', label: 'Create Packs', icon: FiPlus },
          { id: 'events', label: 'Events', icon: FiCalendar },
          { id: 'currencies', label: 'Currencies', icon: FiDollarSign }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-6 py-3 rounded-[1.5rem] text-sm font-bold transition-all ${activeTab === tab.id ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}>
            <SafeIcon icon={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'saved' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-[2rem] border border-gray-100">
            <h2 className="text-2xl font-bold flex items-center gap-2 px-2">
              <SafeIcon icon={FiLayers} className="text-gray-400" /> Saved Event Packs
            </h2>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <SafeIcon icon={FiFilter} className="text-gray-400" />
              <select value={filterEventId} onChange={(e) => setFilterEventId(e.target.value)} className="w-full md:w-64 px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl outline-none font-bold text-sm">
                <option value="all">All Events</option>
                {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPacks.map(pack => {
              const rate = pack.currency?.crystal_rate || 0;
              const totalCostCrystals = pack.items.reduce((acc, it) => acc + (it.unit_event_cost * it.quantity * rate), 0);
              const eff = totalCostCrystals > 0 ? (pack.target_market_worth / totalCostCrystals) : 0;
              let status = 'none';
              if (totalCostCrystals > 0) {
                if (eff >= 1.15) status = 'worth';
                else if (eff < 1.10) status = 'avoid';
                else status = 'breakeven';
              }
              const config = getStatusConfig(status);

              return (
                <div key={pack.id} className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-xl transition-all group overflow-hidden flex flex-col">
                  <div className="p-6 flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors">{pack.name}</h4>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-lg text-[9px] font-black uppercase">{pack.event?.name}</span>
                          <span className="px-2 py-0.5 bg-gray-50 text-gray-500 rounded-lg text-[9px] font-black uppercase">{pack.currency?.name}</span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => { setCurrentPackId(pack.id); setPackName(pack.name); setSelectedEventId(pack.event_id); setSelectedCurrencyId(pack.currency_id); setSelectedItems(pack.items); setActiveTab('create'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="p-2 text-gray-300 hover:text-black"><SafeIcon icon={FiEdit3} /></button>
                        <button onClick={async () => { if (confirm('Delete?')) { await supabase.from(TABLES.PACKS).delete().eq('id', pack.id); fetchData(); } }} className="p-2 text-gray-300 hover:text-red-500"><SafeIcon icon={FiTrash2} /></button>
                      </div>
                    </div>

                    <div className="space-y-1.5 mb-4">
                      {pack.items.slice(0, 3).map((it, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-gray-500">{it.quantity}x {it.item_name}</span>
                          <span className="font-mono font-bold">{(it.quantity * it.e_cost).toLocaleString()} C</span>
                        </div>
                      ))}
                      {pack.items.length > 3 && <p className="text-[9px] text-gray-400 font-bold uppercase">+{pack.items.length - 3} more items</p>}
                    </div>

                    <div className="pt-4 border-t border-gray-50 flex justify-between items-end">
                      <div>
                        <p className="text-[8px] font-black text-gray-400 uppercase">Market Worth</p>
                        <p className="font-mono font-black text-gray-900">{pack.target_market_worth?.toLocaleString()} C</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] font-black text-gray-400 uppercase">Efficiency</p>
                        <p className={`font-mono font-black ${status === 'worth' ? 'text-green-600' : status === 'avoid' ? 'text-red-600' : 'text-amber-500'}`}>
                          {eff.toFixed(2)}x
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {config && (
                    <div className={`${config.color} py-2.5 px-6 flex items-center justify-center gap-2 text-white`}>
                      <SafeIcon icon={config.icon} className="text-xs" />
                      <span className="text-[10px] font-black uppercase tracking-widest">{config.label}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'create' && (
        <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl space-y-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Pack Name</label>
              <input value={packName} onChange={(e) => setPackName(e.target.value)} placeholder="Summer Starter Pack" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-medium" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Link to Event</label>
              <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-bold">
                <option value="">Select Event...</option>
                {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Calculation Currency</label>
              <select value={selectedCurrencyId} onChange={(e) => setSelectedCurrencyId(e.target.value)} className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-bold">
                <option value="">Select Currency...</option>
                {currencies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.crystal_rate} C)</option>)}
              </select>
            </div>
          </div>

          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Add items to bundle..." className="w-full pl-11 pr-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none" />
            <AnimatePresence>
              {searchQuery && (
                <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl max-h-60 overflow-y-auto">
                  {allAvailable.filter(i => i.item_name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 10).map((item, idx) => (
                    <button key={idx} onClick={() => { if (!selectedItems.find(si => si.item_name === item.item_name)) { setSelectedItems([...selectedItems, { ...item, quantity: 1, unit_event_cost: 0 }]); } setSearchQuery(''); }} className="w-full px-5 py-3 text-left hover:bg-blue-50 flex justify-between items-center border-b border-gray-50 last:border-0 transition-colors">
                      <span className="font-bold text-gray-900">{item.item_name}</span>
                      <span className="text-xs font-mono text-blue-600">{item.e_cost} C</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="space-y-3">
            {selectedItems.map((item, idx) => (
              <div key={idx} className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <h4 className="font-bold text-gray-900">{item.item_name}</h4>
                  <p className="text-[10px] text-gray-400 uppercase font-black">Market: {item.e_cost} C / unit</p>
                </div>
                <div className="w-20">
                  <label className="text-[8px] font-black text-gray-400 uppercase">Qty</label>
                  <input type="number" value={item.quantity} onChange={(e) => setSelectedItems(selectedItems.map((si, i) => i === idx ? { ...si, quantity: parseInt(e.target.value) || 1 } : si))} className="w-full px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-xs outline-none font-bold" />
                </div>
                <div className="w-32">
                  <label className="text-[8px] font-black text-gray-400 uppercase">Cost in {activeCurrency?.name || 'Currency'}</label>
                  <input type="number" value={item.unit_event_cost} onChange={(e) => setSelectedItems(selectedItems.map((si, i) => i === idx ? { ...si, unit_event_cost: parseFloat(e.target.value) || 0 } : si))} className="w-full px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-xs outline-none font-bold" />
                </div>
                <button onClick={() => setSelectedItems(selectedItems.filter((_, i) => i !== idx))} className="pb-1.5 text-gray-300 hover:text-red-500 transition-colors"><SafeIcon icon={FiTrash2} /></button>
              </div>
            ))}
          </div>

          <div className="pt-6 border-t border-gray-100 grid md:grid-cols-4 gap-4">
            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Market Worth</p>
              <p className="text-2xl font-mono font-black">{calculations.marketWorth.toLocaleString()} C</p>
            </div>
            <div className="p-5 bg-blue-50 rounded-2xl border border-blue-100">
              <p className="text-[10px] font-black text-blue-600 uppercase mb-1">Calculated Cost</p>
              <p className="text-2xl font-mono font-black text-blue-700">{calculations.eventSpendCrystals.toLocaleString()} C</p>
            </div>
            <div className={`p-5 rounded-2xl border ${calculations.saving >= 0 ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
              <p className="text-[10px] font-black text-gray-500 uppercase mb-1">Savings</p>
              <p className={`text-2xl font-mono font-black ${calculations.saving >= 0 ? 'text-green-600' : 'text-red-600'}`}>{Math.abs(calculations.saving).toLocaleString()} C</p>
            </div>
            <div className="p-5 bg-purple-50 rounded-2xl border border-purple-100">
              <p className="text-[10px] font-black text-purple-600 uppercase mb-1">Efficiency</p>
              <p className="text-2xl font-mono font-black text-purple-700">{calculations.efficiency.toFixed(2)}x</p>
            </div>
          </div>

          {/* Recommendation Banner */}
          {selectedItems.length > 0 && calculations.eventSpendCrystals > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`p-6 rounded-[2rem] border flex items-center justify-between text-white shadow-xl ${getStatusConfig(calculations.status).color}`}>
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={getStatusConfig(calculations.status).icon} className="text-3xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tight leading-none mb-1">
                    {getStatusConfig(calculations.status).label}
                  </h3>
                  <p className="text-sm opacity-90 font-medium">
                    {calculations.status === 'worth' 
                      ? `Exceptional value! This pack is ${((calculations.efficiency - 1) * 100).toFixed(0)}% better than market rates.` 
                      : calculations.status === 'avoid'
                      ? `Poor value. Market rates are actually cheaper than this event pack.`
                      : `Marginal value. Buy only if you strictly need these specific items.`}
                  </p>
                </div>
              </div>
              <div className="text-right hidden md:block border-l border-white/20 pl-8">
                <p className="text-[10px] font-black uppercase opacity-60">Benchmarks</p>
                <div className="flex flex-col text-xs font-bold">
                  <span className="text-green-300">&gt; 115% Profit</span>
                  <span className="text-red-300">&lt; 110% Profit</span>
                </div>
              </div>
            </motion.div>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={resetPackForm} className="px-6 py-3 rounded-xl text-sm font-bold text-gray-400 hover:bg-gray-50">Cancel</button>
            <button onClick={savePack} disabled={!packName || !selectedCurrencyId || !selectedEventId} className="px-10 py-3 bg-black text-white rounded-xl text-sm font-bold shadow-xl shadow-black/10 disabled:opacity-20 transition-all hover:scale-[1.02] active:scale-[0.98]">Save Pack Goal</button>
          </div>
        </div>
      )}

      {/* Events & Currencies Tabs remain unchanged in logic but wrapped in same styling... */}
      {activeTab === 'events' && (
        <div className="grid md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl h-fit space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">Manage Events</h2>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Event Name</label>
                <input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Summer Festival 2024" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-medium" />
              </div>
              <div className="flex gap-2">
                {editingEvent && <button onClick={() => { setEditingEvent(null); setEventName(''); }} className="flex-1 py-3 bg-gray-100 rounded-xl text-sm font-bold">Cancel</button>}
                <button onClick={saveEvent} className="flex-[2] py-3 bg-black text-white rounded-xl text-sm font-bold shadow-lg shadow-black/10">Save Event</button>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {events.map(e => (
              <div key={e.id} className="bg-white p-6 rounded-3xl border border-gray-100 flex justify-between items-center group shadow-sm">
                <span className="font-bold text-gray-900">{e.name}</span>
                <div className="flex gap-2">
                  <button onClick={() => { setEditingEvent(e); setEventName(e.name); }} className="p-2 text-gray-300 hover:text-blue-600 transition-colors"><SafeIcon icon={FiEdit3} /></button>
                  <button onClick={async () => { if (confirm('Delete?')) { await supabase.from(TABLES.EVENTS).delete().eq('id', e.id); fetchData(); } }} className="p-2 text-gray-300 hover:text-red-500 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'currencies' && (
        <div className="grid md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl h-fit space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">Manage Currencies</h2>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Currency Name</label>
                <input value={currencyName} onChange={(e) => setCurrencyName(e.target.value)} placeholder="e.g. Magic Star" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-medium" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Crystal Value</label>
                <input type="number" value={currencyRate} onChange={(e) => setCurrencyRate(parseFloat(e.target.value) || 0)} className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl font-mono font-bold outline-none" />
              </div>
              <div className="flex gap-2">
                {editingCurrency && <button onClick={() => { setEditingCurrency(null); setCurrencyName(''); }} className="flex-1 py-3 bg-gray-100 rounded-xl text-sm font-bold">Cancel</button>}
                <button onClick={saveCurrency} className="flex-[2] py-3 bg-black text-white rounded-xl text-sm font-bold shadow-lg shadow-black/10">Save Currency</button>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {currencies.map(c => (
              <div key={c.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex justify-between items-center group">
                <div>
                  <h4 className="font-bold text-gray-900">{c.name}</h4>
                  <p className="text-xs font-mono text-blue-600 font-bold">1 {c.name} = {c.crystal_rate} Crystal</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditingCurrency(c); setCurrencyName(c.name); setCurrencyRate(c.crystal_rate); }} className="p-2 text-gray-400 hover:text-blue-600 transition-colors"><SafeIcon icon={FiEdit3} /></button>
                  <button onClick={async () => { if (confirm('Delete?')) { await supabase.from(TABLES.CURRENCIES).delete().eq('id', c.id); fetchData(); } }} className="p-2 text-gray-300 hover:text-red-500 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}